// src/pages/audit-logs/index.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Form
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import { useGetAuditLogs, useUpdateAuditLog } from "../../api/users";
import { Badge } from "reactstrap";
import toastr from "toastr";

const AuditLogsList = () => {
  document.title = "Audit Logs | Admin";

  // server-side controls
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);

  // Filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [userRole, setUserRole] = useState("");

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editDescription, setEditDescription] = useState("");

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAuditLogs();
  const loading = isLoading || isPending;

  // mutation hook (UPDATE)
  const updateAuditLog = useUpdateAuditLog();

  // Build a stable fetch key and guard against duplicate calls (e.g., StrictMode double invoke)
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit,
    startDate,
    endDate,
    search,
    userRole
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      search: search.trim() || undefined,
      userRole: userRole || undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  // API response structure: { data: [...], totalRecords: 36, results: 36 }
  const auditLogs = Array.isArray(data?.data) ? data.data : [];
  const total = data?.totalRecords ?? data?.results ?? 0;

  const columns = useMemo(
    () => [
      {
        header: "Timestamp",
        accessorKey: "timestamp",
        enableColumnFilter: false,
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return "-";
          const date = new Date(timestamp);
          return date.toLocaleString();
        }
      },
      {
        header: "Actor",
        accessorKey: "user",
        enableColumnFilter: false,
        cell: (info) => {
          const user = info.getValue();
          if (!user) return "-";
          return (
            <div>
              <div>{user.fullName || "-"}</div>
              <small className="text-muted">{user.email || "-"}</small>
              <div>
                <Badge className="bg-secondary" style={{ fontSize: "0.7rem" }}>
                  {user.role || "-"}
                </Badge>
              </div>
            </div>
          );
        }
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (info) => {
          const action = info.getValue();
          const actionColors = {
            read: "bg-info",
            create: "bg-success",
            update: "bg-warning text-dark",
            delete: "bg-danger"
          };
          const color = actionColors[action?.toLowerCase()] || "bg-secondary";
          return <Badge className={color}>{action || "-"}</Badge>;
        }
      },
      {
        header: "Entity",
        accessorKey: "slugUser",
        enableColumnFilter: false,
        cell: (info) => {
          const slugUser = info.getValue();
          if (!slugUser) return "-";
          return (
            <div>
              <div>{slugUser.fullName || "-"}</div>
              <small className="text-muted">{slugUser.email || "-"}</small>
              <div>
                <Badge className="bg-secondary" style={{ fontSize: "0.7rem" }}>
                  {slugUser.role || "-"}
                </Badge>
              </div>
            </div>
          );
        }
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
        cell: (info) => {
          const description = info.getValue();
          const row = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2">
              <span className="flex-grow-1">{description || "-"}</span>
             
            </div>
          );
        }
      },
      {
        header: "Table",
        accessorKey: "table",
        enableColumnFilter: false
      },
      {
        header: "IP Address",
        accessorKey: "ipAddress",
        enableColumnFilter: false
      }
    ],
    []
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Audit Logs" breadcrumbItem="Audit Logs List" />

        <Row className="mb-3 g-2 align-items-end">
          {/* Search */}
          <Col md={3}>
            <Label className="form-label">Search</Label>
            <Input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          {/* Start Date */}
          <Col md={2}>
            <Label className="form-label">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
            />
          </Col>

          {/* End Date */}
          <Col md={2}>
            <Label className="form-label">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
            />
          </Col>

          {/* User Role */}
          <Col md={2}>
            <Label className="form-label">User Role</Label>
            <Input
              type="select"
              value={userRole}
              onChange={(e) => {
                setUserRole(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="subAdmin">Sub Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="client">Client</option>
              <option value="worker">Worker</option>
            </Input>
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setStartDate("");
                setEndDate("");
                setSearch("");
                setSearchInput("");
                setUserRole("");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  startDate: startDate || undefined,
                  endDate: endDate || undefined,
                  search: search.trim() || undefined,
                  userRole: userRole || undefined
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && auditLogs.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load audit logs.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={auditLogs}
                  // server-side: disable client pagination & global filters
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Edit Description Modal */}
      <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
        <ModalHeader toggle={() => setShowEditModal(false)}>Edit Description</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            if (!editingLog) return;

            updateAuditLog.mutate(
              {
                id: editingLog.id,
                description: editDescription
              },
              {
                onSuccess: () => {
                  toastr.success("Description updated successfully");
                  setShowEditModal(false);
                  setEditingLog(null);
                  setEditDescription("");
                  // Refresh the list
                  mutate({
                    page,
                    limit,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    search: search.trim() || undefined,
                    userRole: userRole || undefined
                  });
                },
                onError: (error) => {
                  toastr.error(error?.response?.data?.message || "Failed to update description");
                }
              }
            );
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label for="edit-description">Description</Label>
              <Input
                id="edit-description"
                type="textarea"
                rows="4"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Enter description..."
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setShowEditModal(false);
                setEditingLog(null);
                setEditDescription("");
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={updateAuditLog.isPending}>
              {updateAuditLog.isPending ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default AuditLogsList;
